/**
 * Geological Analysis Store
 * 
 * Manages geoanalysis state for map visualization:
 * - Analysis markers
 * - Heatmap data
 * - Selected analysis point
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { 
  GeoanalysisMarker, 
  FullAnalysisResult,
  HeatmapCell,
  GoldPotentialZone,
  HierarchicalAnalysisResult,
  HierarchicalZone,
} from '@/types/geoanalysis';

interface GeoanalysisState {
  // Analysis mode
  isAnalysisMode: boolean;
  isSelectingPoint: boolean;
  
  // Selected point for analysis
  selectedPoint: { lat: number; lng: number } | null;
  
  // Analysis results
  markers: GeoanalysisMarker[];
  currentAnalysis: FullAnalysisResult | null;
  
  // Heatmap
  showHeatmap: boolean;
  heatmapCells: HeatmapCell[];
  heatmapOpacity: number;
  
  // High potential zones
  showZones: boolean;
  zones: GoldPotentialZone[];
  selectedZone: GoldPotentialZone | null;
  
  // WebODM task for analysis
  analysisTask: {
    projectId: number;
    taskId: string;
    name: string;
  } | null;
  
  // Hierarchical analysis
  hierarchicalResult: HierarchicalAnalysisResult | null;
  showGrid: boolean;
  gridOpacity: number;
  selectedGridZone: HierarchicalZone | null;
  
  // Loading states
  isAnalyzing: boolean;
  isGeneratingHeatmap: boolean;
  isRunningHierarchical: boolean;
  
  // Sidebar panel state
  showAnalysisPanel: boolean;
  
  // Actions
  setAnalysisMode: (enabled: boolean) => void;
  startPointSelection: () => void;
  stopPointSelection: () => void;
  
  setSelectedPoint: (point: { lat: number; lng: number } | null) => void;
  
  setAnalysisTask: (task: { projectId: number; taskId: string; name: string } | null) => void;
  
  addMarker: (marker: GeoanalysisMarker) => void;
  removeMarker: (id: string) => void;
  clearMarkers: () => void;
  
  setCurrentAnalysis: (analysis: FullAnalysisResult | null) => void;
  
  setShowHeatmap: (show: boolean) => void;
  setHeatmapCells: (cells: HeatmapCell[]) => void;
  setHeatmapOpacity: (opacity: number) => void;
  clearHeatmap: () => void;
  
  setShowZones: (show: boolean) => void;
  setZones: (zones: GoldPotentialZone[]) => void;
  setSelectedZone: (zone: GoldPotentialZone | null) => void;
  
  setIsAnalyzing: (loading: boolean) => void;
  setIsGeneratingHeatmap: (loading: boolean) => void;
  setIsRunningHierarchical: (loading: boolean) => void;
  
  setShowAnalysisPanel: (show: boolean) => void;
  
  // Hierarchical analysis actions
  setHierarchicalResult: (result: HierarchicalAnalysisResult | null) => void;
  setShowGrid: (show: boolean) => void;
  setGridOpacity: (opacity: number) => void;
  setSelectedGridZone: (zone: HierarchicalZone | null) => void;
  clearHierarchical: () => void;
  
  // Add marker from analysis result
  addMarkerFromAnalysis: (lat: number, lng: number, analysis: FullAnalysisResult) => void;
  
  // Clear all state
  clearAll: () => void;
}

function getMarkerType(score: number): 'high_potential' | 'medium_potential' | 'low_potential' {
  if (score >= 70) return 'high_potential';
  if (score >= 40) return 'medium_potential';
  return 'low_potential';
}

export const useGeoanalysisStore = create<GeoanalysisState>()(
  devtools(
    persist(
      (set, _get) => ({
        // Initial state
        isAnalysisMode: false,
        isSelectingPoint: false,
        selectedPoint: null,
        markers: [],
        currentAnalysis: null,
        showHeatmap: false,
        heatmapCells: [],
        heatmapOpacity: 0.6,
        showZones: true,
        zones: [],
        selectedZone: null,
        analysisTask: null,
        hierarchicalResult: null,
        showGrid: true,
        gridOpacity: 0.5,
        selectedGridZone: null,
        isAnalyzing: false,
        isGeneratingHeatmap: false,
        isRunningHierarchical: false,
        showAnalysisPanel: false,
        
        // Actions
        setAnalysisMode: (enabled) => set({ 
          isAnalysisMode: enabled,
          showAnalysisPanel: enabled,
        }),
        
        startPointSelection: () => set({ isSelectingPoint: true }),
        stopPointSelection: () => set({ isSelectingPoint: false }),
        
        setSelectedPoint: (point) => set({ selectedPoint: point }),
        
        setAnalysisTask: (task) => set({ analysisTask: task }),
        
        addMarker: (marker) => set((state) => ({
          markers: [...state.markers, marker],
        })),
        
        removeMarker: (id) => set((state) => ({
          markers: state.markers.filter((m) => m.id !== id),
        })),
        
        clearMarkers: () => set({ markers: [] }),
        
        setCurrentAnalysis: (analysis) => set({ 
          currentAnalysis: analysis,
          showAnalysisPanel: analysis !== null,
        }),
        
        setShowHeatmap: (show) => set({ showHeatmap: show }),
        setHeatmapCells: (cells) => set({ heatmapCells: cells }),
        setHeatmapOpacity: (opacity) => set({ heatmapOpacity: opacity }),
        clearHeatmap: () => set({ heatmapCells: [], showHeatmap: false }),
        
        setShowZones: (show) => set({ showZones: show }),
        setZones: (zones) => set({ zones }),
        setSelectedZone: (zone) => set({ selectedZone: zone }),
        
        setIsAnalyzing: (loading) => set({ isAnalyzing: loading }),
        setIsGeneratingHeatmap: (loading) => set({ isGeneratingHeatmap: loading }),
        setIsRunningHierarchical: (loading) => set({ isRunningHierarchical: loading }),
        
        setShowAnalysisPanel: (show) => set({ showAnalysisPanel: show }),
        
        // Hierarchical analysis actions
        setHierarchicalResult: (result) => set({ 
          hierarchicalResult: result,
          showGrid: result !== null,
        }),
        setShowGrid: (show) => set({ showGrid: show }),
        setGridOpacity: (opacity) => set({ gridOpacity: opacity }),
        setSelectedGridZone: (zone) => set({ selectedGridZone: zone }),
        clearHierarchical: () => set({
          hierarchicalResult: null,
          showGrid: false,
          selectedGridZone: null,
        }),
        
        addMarkerFromAnalysis: (lat, lng, analysis) => {
          const scoring = analysis.scoring;
          if (!scoring) return;
          
          const marker: GeoanalysisMarker = {
            id: `analysis-${Date.now()}`,
            lat,
            lng,
            score: scoring.overall_score,
            type: getMarkerType(scoring.overall_score),
            primary_indicator: scoring.positive_indicators[0]?.type || 'unknown',
            indicators: [...scoring.positive_indicators, ...scoring.negative_indicators],
            recommendation: scoring.summary,
            timestamp: analysis.timestamp,
          };
          
          set((state) => ({
            markers: [...state.markers, marker],
            zones: scoring.zones,
          }));
        },
        
        clearAll: () => set({
          isAnalysisMode: false,
          isSelectingPoint: false,
          selectedPoint: null,
          markers: [],
          currentAnalysis: null,
          showHeatmap: false,
          heatmapCells: [],
          showZones: true,
          zones: [],
          selectedZone: null,
          analysisTask: null,
          hierarchicalResult: null,
          showGrid: false,
          selectedGridZone: null,
          isAnalyzing: false,
          isGeneratingHeatmap: false,
          isRunningHierarchical: false,
          showAnalysisPanel: false,
        }),
      }),
      {
        name: 'geoanalysis-storage',
        partialize: (state) => ({
          // Only persist markers and settings
          markers: state.markers,
          heatmapOpacity: state.heatmapOpacity,
          showZones: state.showZones,
        }),
      }
    ),
    { name: 'geoanalysis' }
  )
);
